
import { formatTime, haversineDistance, dijkstra, bearing, reconstructPath, parseLineString, toCompass, turnInstruction } from './generalHelpers';

export function ensureRouteNameParts(value) {
  if (Array.isArray(value)) {
    return value.map(v => String(v || "").trim()).filter(Boolean);
  }
  const raw = String(value ?? "").trim();
  if (!raw) return [];
  if (raw.startsWith("[") && raw.endsWith("]")) {
    try {
      const arr = JSON.parse(raw.replace(/'/g, '"'));
      if (Array.isArray(arr)) {
        return arr.map(v => String(v || "").trim()).filter(Boolean);
      }
    } catch (_) {
      // ignore parse error and fall back to single value
    }
  }
  return [raw];
}

export function normalizeRouteName(value) {
  const parts = ensureRouteNameParts(value);
  if (!parts.length) return "";
  return parts.join(" - ");
}

export function dedupeRouteNames(names) {
  const out = [];
  (names || []).forEach(n => {
    const val = normalizeRouteName(n);
    if (!val) return;
    if (out[out.length - 1] !== val) out.push(val);
  });
  return out;
}

export function buildRouteDetailsPromptLines(entriesOrMap, escapeFn = (s) => s) {
  const lines = [];
  const entriesArray = Array.isArray(entriesOrMap)
    ? entriesOrMap
    : Object.values(entriesOrMap || {});
  const seen = new Set();
  entriesArray.forEach(entry => {
    if (!entry || seen.has(entry)) return;
    seen.add(entry);
    const displayName = entry?.title?.trim() || entry?.id || entry?.key;
    if (!displayName) return;
    lines.push(`<Routes for ${displayName}>`);
    lines.push("Link name, Link id list")
    const links = Array.isArray(entry?.links) ? entry.links : [];
    let currentName = "";
    let currentIds = [];
    const flush = () => {
      if (!currentName || !currentIds.length) return;
      lines.push(`${escapeFn(currentName)}, ${JSON.stringify(currentIds)}`);
    };
    links.forEach(link => {
      const linkName = normalizeRouteName(link?.name);
      const linkId = String(link?.id ?? link?.linkId ?? "").trim();
      if (!linkName) {
        flush();
        currentName = "";
        currentIds = [];
        return;
      }
      if (linkName !== currentName) {
        flush();
        currentName = linkName;
        currentIds = [];
      }
      if (linkId) currentIds.push(linkId);
    });
    flush();
    lines.push(`</Routes for ${displayName}>`);
    lines.push("");
  });
  return lines;
}


const METERS_PER_DEG_LAT = 111320;
const metersPerDegLng = (lat) => 111320 * Math.cos((lat * Math.PI) / 180);

function pointToSegmentDistMeters(pxLng, pyLat, axLng, ayLat, bxLng, byLat) {
  // local meters frame centered at (pyLat, pxLng)
  const kx = metersPerDegLng(pyLat), ky = METERS_PER_DEG_LAT;
  const Ax = (axLng - pxLng) * kx, Ay = (ayLat - pyLat) * ky;
  const Bx = (bxLng - pxLng) * kx, By = (byLat - pyLat) * ky;
  const vx = Bx - Ax, vy = By - Ay;
  const wx = -Ax,     wy = -Ay;
  const vv = vx*vx + vy*vy || 1e-9;
  let t = (wx*vx + wy*vy) / vv;
  t = Math.max(0, Math.min(1, t));
  const dx = Ax + t*vx, dy = Ay + t*vy;
  return Math.hypot(dx, dy);
}

export function nearestLinkToPoint(lat, lng, links) {
  let best = null;
  for (const row of links || []) {
    const wkt = row.geometry;
    if (!wkt) continue;
    const coords = wkt
      .replace(/^LINESTRING\s*\(/i, "")
      .replace(/\)$/, "")
      .split(",")
      .map(pt => {
        const [xLng, yLat] = pt.trim().split(" ").map(Number);
        return { lat: yLat, lng: xLng };
      });

    let minSeg = Infinity;
    for (let i = 0; i < coords.length - 1; i++) {
      const a = coords[i], b = coords[i+1];
      const d = pointToSegmentDistMeters(lng, lat, a.lng, a.lat, b.lng, b.lat);
      if (d < minSeg) minSeg = d;
    }
    if (best === null || minSeg < best.dist) {
      best = { dist: minSeg, link: row };
    }
  }
  return best; // { dist, link } or null
}

/**
 * Pick the nearest graph node (and top-K candidates) using:
 *  1) nearest link to (lat,lng),
 *  2) Dijkstra from both endpoints,
 *  3) optional street-name tie-breaker among top-K
 *
 * @returns { nearestNodeId, candidates, nearestLink }
 *  - candidates: [{ id, lat, lng, d, bestSim, score }]
 */
export function pickNearestGraphNode({
  lat,
  lng,
  graphData,                 // { links, nodes, adjList }
  k = 6,
  podcastNodeIds,           // Set<string> of allowed node ids
  weightAccessor = (edge) => Number(edge.weight || edge.link?.length || 0),
  streetText,               // optional extracted street string
  linkStreetName,           // fn(linkRow) -> string | null
  streetSimilarity          // fn(a,b) -> number in [0..1]
}) {
  if (!graphData?.links?.length || !graphData?.nodes || !graphData?.adjList) {
    return { nearestNodeId: null, candidates: [], nearestLink: null };
  }

  // 1) nearest link to click
  const nearest = nearestLinkToPoint(lat, lng, graphData.links);
  if (!nearest || !nearest.link) {
    return { nearestNodeId: null, candidates: [], nearestLink: null };
  }

  console.log("NearestLink", nearest.link)

  // 2) run Dijkstra from both endpoints
  const srcA = String(nearest.link.start_node_id);
  const srcB = String(nearest.link.end_node_id);
  const distFromA = dijkstra(graphData.adjList, srcA, weightAccessor);
  const distFromB = dijkstra(graphData.adjList, srcB, weightAccessor);

  console.log("DistFromA", distFromA)

  // 3) collect podcast nodes with min distance
  const all = [];
  for (const [idStr, node] of Object.entries(graphData.nodes)) {
    console.log("print node", idStr, node)
    if (!podcastNodeIds?.has(String(idStr))) continue;
    const da = distFromA.distances[String(idStr)];
    const db = distFromB.distances[String(idStr)];
    const dGraph = Math.min(
      Number.isFinite(da) ? da : Infinity,
      Number.isFinite(db) ? db : Infinity
    );
    console.log('dGraph', dGraph)
    if (Number.isFinite(dGraph)) {
      all.push({ id: idStr, lat: node.lat, lng: node.lng, d: dGraph });
    }
  }

  // 4) top-K by graph distance
  all.sort((a, b) => a.d - b.d);
  console.log("all: ", all)

  const top = all.slice(0, k);

  console.log("top: ", top)

  // 5) optional street-name scoring within top-K
  let scored = top.map(c => ({ ...c, bestSim: 0, score: -c.d }));
  if (streetText && linkStreetName && streetSimilarity) {
    scored = top.map(c => {
      const adj = (graphData.links || []).filter(
        L => String(L.start_node_id) === String(c.id) || String(L.end_node_id) === String(c.id)
      );
      const names = adj.map(linkStreetName).filter(Boolean);
      const bestSim = names.reduce((mx, n) => Math.max(mx, streetSimilarity(streetText, n)), 0);
      const w = 200; // same bias as before
      const score = bestSim * w - c.d;
      return { ...c, bestSim, score };
    });
  }

  scored.sort((a,b) => b.score - a.score);
  let chosen = scored[0] || null;

  // prefer strong name match if close to best distance
  if (scored.length) {
    const bestD = scored.map(s => s.d).sort((a,b)=>a-b)[0];
    const strong = scored.filter(s => s.bestSim >= 0.8 && s.d <= bestD + 120);
    if (strong.length) {
      strong.sort((a,b) => a.d - b.d);
      chosen = strong[0];
    }
  }

  return {
    nearestNodeId: chosen?.id || null,
    candidates: scored,
    nearestLink: nearest.link
  };
}


/* ===== Route diversity helpers (plain JS) ===== */

export function getNearestGraphNodeId(lat, lng, nodesById) {
    let best = null;
    let minD = Infinity;
    for (const n of Object.values(nodesById)) {
      const d = haversineDistance(lat, lng, n.lat, n.lng);
      if (d < minD) { minD = d; best = n.id; }
    }
    return best;
  }
  
  export function getEdge(adjList, u, v) {
    return adjList[u]?.find(e => e.node === v) ?? null;
  }
  
  export function pathToLinkIds(adjList, nodePath) {
    const ids = [];
    for (let i = 0; i < nodePath.length - 1; i++) {
      const e = getEdge(adjList, nodePath[i], nodePath[i + 1]);
      if (e?.link?.link_id != null) ids.push(e.link.link_id);
    }
    return ids;
  }
  
  export function pathCost(adjList, nodePath) {
    let s = 0;
    for (let i = 0; i < nodePath.length - 1; i++) {
      const e = getEdge(adjList, nodePath[i], nodePath[i + 1]);
      if (!e) return Infinity;
      s += Number(e.weight ?? 0);
    }
    return s;
  }
  
  // |A ∩ B| / min(|A|, |B|) using link_id lists
  export function linkOverlapFractionByIds(aIds, bIds) {
    if (!aIds.length || !bIds.length) return 0;
    const setB = new Set(bIds);
    let inter = 0;
    for (const id of aIds) if (setB.has(id)) inter++;
    return inter / Math.min(aIds.length, bIds.length);
  }
  
// Sum of shared *prefix* weights until divergence or window exhausted
export function sharedPrefixCostWithinWindow(adjList, pathA, pathB, windowCost) {
    let cost = 0;
    const n = Math.min(pathA.length, pathB.length) - 1;
    for (let i = 0; i < n; i++) {
      const a1 = pathA[i], a2 = pathA[i + 1];
      const b1 = pathB[i], b2 = pathB[i + 1];
      if (a1 !== b1 || a2 !== b2) break;
      const e = getEdge(adjList, a1, a2);
      if (!e) break;
      const w = Number(e.weight ?? 0);
      if (cost + w > windowCost) { cost = windowCost; break; }
      cost += w;
      if (cost >= windowCost) break;
    }
    return Math.min(cost, windowCost);
}
  
  export function earlyBranchingPass(adjList, pathA, pathB, earlyWindowCost, earlySharedMaxFrac) {
    if (earlyWindowCost <= 0) return true;
    const shared = sharedPrefixCostWithinWindow(adjList, pathA, pathB, earlyWindowCost);
    const frac = shared / earlyWindowCost;
    return frac <= earlySharedMaxFrac;
  }
  
  export function dissimilarEnoughByIds(aIds, bIds, delta) {
    const overlap = linkOverlapFractionByIds(aIds, bIds);
    const dissim = 1 - overlap;
    return dissim >= delta;
  }
  
  // Clone adjList and apply penalties: weight' = weight * (1 + penaltyMap[link_id])
  export function makePenaltyAdjList(baseAdj, penaltyMap) {
    const out = {};
    for (const [u, edges] of Object.entries(baseAdj)) {
      out[u] = edges.map(e => {
        const lid = e?.link?.link_id;
        const mult = 1 + (penaltyMap[lid] ?? 0);
        return { ...e, weight: Number(e.weight ?? 0) * mult };
      });
    }
    return out;
  }
  
  // Generate up to K near-optimal alternatives for a destination via iterative penalization.
  // NOTE: expects dijkstra, reconstructPath, pathCost, pathToLinkIds to be available (imported in this file).
  export function generateNearOptimalAlternativesForDestination(opts) {
    const {
      baseAdjList,
      originNodeId,
      destId,
      baseBestCost,
      rho,
      K,
      penaltyAlpha
    } = opts;
  
    const alts = [];
    const penalty = Object.create(null);
  
    for (let k = 0; k < K; k++) {
      const adjWithPenalty = makePenaltyAdjList(baseAdjList, penalty);
      const { distances, previous } = dijkstra(adjWithPenalty, originNodeId);
      const nodePath = reconstructPath(previous, originNodeId, destId);
      if (nodePath.length < 2) break;
  
      const cost = pathCost(baseAdjList, nodePath);
      const linkIds = pathToLinkIds(baseAdjList, nodePath);

      /*
      console.log(linkIds)
      console.log("Cost:", cost)
      console.log("Base Best Cost:", baseBestCost)
        */

      //if (cost <= (1 + rho) * baseBestCost) {
        //console.log("Pass cost test")
        alts.push({ node_path: nodePath, link_ids: linkIds, total_cost: cost });
      //}
  
      // Penalize edges of this path to push next iteration elsewhere
      for (let i = 0; i < nodePath.length - 1; i++) {
        const e = getEdge(baseAdjList, nodePath[i], nodePath[i + 1]);
        const lid = e?.link?.link_id;
        if (lid != null) penalty[lid] = (penalty[lid] ?? 0) + penaltyAlpha;
      }
    }
  
    return alts;
  }
